import React, { useState, useEffect, useMemo, useCallback , useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Autocomplete,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  Stack,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  DialogTitle,
  DialogActions,
  Menu,
  MenuItem,
} from '@mui/material';
import { downloadTranscript } from '../../utils/transcriptExport';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import DraftsIcon from '@mui/icons-material/Drafts';
import ArchiveIcon from '@mui/icons-material/Archive';
import SaveIcon from '@mui/icons-material/Save';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import ReactMarkdown from 'react-markdown';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { AI_ENGINE_URL, BACKEND_URL } from '../../utils/apiConfig';
import { searchOnenote, getOnenotePageText } from '../../utils/onenoteSearch';
import { searchMail, getConversationText, getMailBody } from '../../utils/graphMailSearch';
import { schoonMailTekst } from '../../utils/mailTekst';
import { verwerkMailBijlagen, bijlagenMelding } from '../../utils/mailBijlagen';
import StartProjectDialog from './StartProjectDialog';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { parseUtcDate } from '../../utils/utcDate';
import { createTasksBatch as createMicrosoftTodoTasks, initMicrosoftTodoService } from '../../utils/microsoftTodoService';
import { saveEmailAsDraft } from '../../utils/graphMailService';
import { formatDateISO } from '../../utils/weekUtils';

const TASK_TYPE_OPTIONS = ['Preproductie', 'Draaidag', 'Montage', 'Animatie', 'Deadline', 'Vrije dag'];
const TASK_PRIO_OPTIONS = ['Afspraak', 'Vrij/intern', 'Draaidag', 'Prio 1', 'Prio 2', 'Prio 3'];

// variant: 'dialog' (popup vanuit de lijst) | 'page' (volwaardige pagina /MeetingDashboard/:id)
function MeetingDetail({ open, onClose, meeting, onUpdate, onDelete, variant = 'dialog' }) {
  const { instance: msalInstance, accounts } = useMsal();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftLink, setDraftLink] = useState(null); // webLink van het Outlook-concept

  // Editable meeting fields
  const [editTitle, setEditTitle] = useState('');
  const [editParticipants, setEditParticipants] = useState('');
  const [titleDirty, setTitleDirty] = useState(false);
  const [participantsDirty, setParticipantsDirty] = useState(false);

  // Transcript (lokaal bewerkbaar via speaker-rename)
  const [transcript, setTranscript] = useState('');
  const [downloadAnker, setDownloadAnker] = useState(null);
  const [speakerRenames, setSpeakerRenames] = useState({});

  // Summary
  const [summary, setSummary] = useState('');
  // Een "⏳ Transcriptie bezig"-regel is een plaatsvervanger: er staat nog geen
  // echte tekst in. Alles wat op het transcript leunt (samenvatting, acties,
  // mail) is daar zinloos of — erger — verzonnen (Joris, 10 sep 2026).
  const nogBezig = (() => {
    try {
      const extra = typeof meeting?.extra_data === 'string' ? JSON.parse(meeting.extra_data) : (meeting?.extra_data || {});
      if (extra?.verwerking === 'bezig') return true;
    } catch { /* geen json */ }
    return String(meeting?.title || '').startsWith('⏳');
  })();
  const [summaryViewMode, setSummaryViewMode] = useState('preview');
  const [summarySaving, setSummarySaving] = useState(false);
  const [savedSummary, setSavedSummary] = useState('');

  const [generatingSummary, setGeneratingSummary] = useState(false);

  // Email
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [emailInstruction, setEmailInstruction] = useState('');
  const [adjustingEmail, setAdjustingEmail] = useState(false);

  // Tasks
  const [actionItems, setActionItems] = useState([]);
  const [taskDestinations, setTaskDestinations] = useState({});
  const [taskAssignees, setTaskAssignees] = useState({});
  const [taskDetails, setTaskDetails] = useState({});
  const [globalTaskProject, setGlobalTaskProject] = useState('');
  const [globalTaskProjectInput, setGlobalTaskProjectInput] = useState('');
  const [pendingSuggestedNames, setPendingSuggestedNames] = useState({});
  const [newTask, setNewTask] = useState('');
  const [savingTasks, setSavingTasks] = useState(false);
  const [extractingActions, setExtractingActions] = useState(false);
  const [people, setPeople] = useState([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [suggestingAssignee, setSuggestingAssignee] = useState(null);
  const [suggestingProject, setSuggestingProject] = useState(false);
  const [suggestedProject, setSuggestedProject] = useState(null);

  // Project
  const [projects, setProjects] = useState([]);
  // Gecombineerde koppel-zoeker (projecten + offertes/leads), server-side.
  const [koppelOpties, setKoppelOpties] = useState([]);
  const [koppelZoekt, setKoppelZoekt] = useState(false);
  const koppelTimer = useRef(null);
  const koppelZoekSeq = useRef(0);
  const zoekKoppelOpties = useCallback((q) => {
    if (koppelTimer.current) clearTimeout(koppelTimer.current);
    const seq = ++koppelZoekSeq.current; // trage 'ka'-response mag 'kadijk' niet overschrijven
    if (!q || q.trim().length < 2) { setKoppelOpties([]); setKoppelZoekt(false); return; }
    koppelTimer.current = setTimeout(async () => {
      setKoppelZoekt(true);
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/koppel-zoek?q=${encodeURIComponent(q.trim())}`);
        const d = await leesJson(r, 'Koppel-zoeken');
        if (seq === koppelZoekSeq.current) setKoppelOpties(d.opties || []);
      } catch (e) { if (seq === koppelZoekSeq.current) { meldFout(e, 'Koppel-zoeken'); setKoppelOpties([]); } }
      finally { if (seq === koppelZoekSeq.current) setKoppelZoekt(false); }
    }, 300);
  }, []);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedProjectInput, setSelectedProjectInput] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [notes, setNotes] = useState('');
  const [leadClients, setLeadClients] = useState([]);
  const [startProjectOpen, setStartProjectOpen] = useState(false);

  // Expanded sections
  const [expanded, setExpanded] = useState({});

  // Exclusief accordion-gedrag: één sectie open → de rest klapt dicht.
  const toggleSection = (section) => {
    setExpanded(prev => (prev[section] ? {} : { [section]: true }));
  };

  // ── Project options ──────────────────────────────────────────────
  const projectOptions = useMemo(() => {
    return (projects || []).map((project) => {
      const label = project.fields?.Projectnr_en_naam || project.fields?.Projectnummer || project.id;
      return { id: project.id, label };
    });
  }, [projects]);

  const projectOptionsWithNone = useMemo(() => {
    return [{ id: '', label: 'Geen project' }, ...projectOptions];
  }, [projectOptions]);

  const getProjectOption = useCallback(
    (id) => projectOptionsWithNone.find((opt) => opt.id === id) || projectOptionsWithNone[0],
    [projectOptionsWithNone]
  );

  const getProjectLabel = useCallback(
    (id) => getProjectOption(id)?.label || '',
    [getProjectOption]
  );

  // ── Parse action items with assignee names ───────────────────────
  const parseActionItemsWithAssignees = (items) => {
    const tasks = [];
    const suggestedAssignees = {};
    let currentAssignee = null;
    for (const item of items) {
      const trimmed = item.trim();
      const nameMatch = trimmed.match(/^\*\*([^*]+)\*\*$/);
      if (nameMatch) { currentAssignee = nameMatch[1].trim(); continue; }
      if (/^[A-Z][a-z]+ [A-Z][a-z]+$/.test(trimmed)) { currentAssignee = trimmed; continue; }
      if (trimmed.length < 5) continue;
      const taskIndex = tasks.length;
      tasks.push(trimmed);
      if (currentAssignee) suggestedAssignees[taskIndex] = currentAssignee;
    }
    return { tasks, suggestedAssignees };
  };

  const findPersonByName = (name, peopleList) => {
    if (!name || !peopleList?.length) return null;
    const nameLower = name.toLowerCase();
    let match = peopleList.find(p => p.naam?.toLowerCase() === nameLower);
    if (match) return match.id;
    const nameParts = nameLower.split(' ');
    match = peopleList.find(p => {
      const personName = p.naam?.toLowerCase() || '';
      return nameParts.some(part => personName.includes(part) && part.length > 2);
    });
    return match?.id || null;
  };

  const defaultTaskStartDate = useMemo(() => {
    if (meeting?.meeting_date) {
      const d = parseUtcDate(meeting.meeting_date);
      if (d && !Number.isNaN(d.getTime())) return d.toISOString().split('T')[0];
    }
    return formatDateISO(new Date());
  }, [meeting?.meeting_date]);

  // ── Initialize when meeting changes ──────────────────────────────
  useEffect(() => {
    if (meeting) {
      setEditTitle(meeting.title || '');
      setEditParticipants(meeting.participants || '');
      setTitleDirty(false);
      setParticipantsDirty(false);
      setTranscript(meeting.transcript || '');
      setSpeakerRenames({});
      setSummary(meeting.summary || '');
      setSavedSummary(meeting.summary || '');
      setSummaryViewMode('preview');
      setEmailSubject(meeting.email_subject_draft || '');
      setEmailBody(meeting.email_body_draft || '');
      setNotes('');
      setSelectedProject('');
      setSelectedProjectInput('');
      setSuggestedProject(null);
      setStartProjectOpen(false);

      const rawItems = meeting.action_items || [];
      const { tasks, suggestedAssignees } = parseActionItemsWithAssignees(rawItems);
      setActionItems(tasks);
      const initialDestinations = {};
      const initialAssignees = {};
      tasks.forEach((_, idx) => { initialDestinations[idx] = 'skip'; initialAssignees[idx] = ''; });
      setTaskDestinations(initialDestinations);

      if (people.length > 0 && Object.keys(suggestedAssignees).length > 0) {
        const autoAssignments = {};
        Object.entries(suggestedAssignees).forEach(([indexStr, suggestedName]) => {
          const personId = findPersonByName(suggestedName, people);
          if (personId) autoAssignments[parseInt(indexStr)] = personId;
        });
        setTaskAssignees({ ...initialAssignees, ...autoAssignments });
        setPendingSuggestedNames({});
      } else {
        setTaskAssignees(initialAssignees);
        setPendingSuggestedNames(suggestedAssignees);
      }

      // Auto-expand sections that have content
      setExpanded({
        summary: !!meeting.summary,
        tasks: !!(meeting.action_items?.length),
        email: !!(meeting.email_subject_draft || meeting.email_body_draft),
        project: false,
      });
    }
  }, [meeting]);

  // Init Microsoft Todo + load people
  useEffect(() => {
    if (msalInstance && accounts.length > 0) initMicrosoftTodoService(msalInstance, accounts[0]);
  }, [msalInstance, accounts]);

  useEffect(() => {
    if (open && meeting && people.length === 0) loadPeople();
  }, [open, meeting]);

  useEffect(() => {
    if (open && meeting && projects.length === 0) loadProjects();
  }, [open, meeting]);

  // Auto-link project from task global project
  useEffect(() => {
    if (globalTaskProject && !selectedProject) {
      setSelectedProject(globalTaskProject);
      setSelectedProjectInput(getProjectLabel(globalTaskProject));
    }
  }, [globalTaskProject, selectedProject, getProjectLabel]);

  // ── Data loaders ─────────────────────────────────────────────────
  const loadPeople = async () => {
    setLoadingPeople(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/personen`);
      if (response.ok) {
        const data = await response.json();
        const loadedPeople = data.people || [];
        setPeople(loadedPeople);
        if (Object.keys(pendingSuggestedNames).length > 0) {
          const autoAssignments = {};
          Object.entries(pendingSuggestedNames).forEach(([indexStr, suggestedName]) => {
            const personId = findPersonByName(suggestedName, loadedPeople);
            if (personId) autoAssignments[parseInt(indexStr)] = personId;
          });
          if (Object.keys(autoAssignments).length > 0) {
            setTaskAssignees(prev => ({ ...prev, ...autoAssignments }));
          }
          setPendingSuggestedNames({});
        }
      }
    } catch (err) { console.error('Error loading people:', err); }
    finally { setLoadingPeople(false); }
  };

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/projects`);
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (err) { console.error('Error loading projects:', err); }
    finally { setLoadingProjects(false); }
  };

  const loadLeadClients = async () => {
    try {
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/airtable/clients?with_projects=false`);
      if (resp.ok) {
        const data = await resp.json();
        setLeadClients((data.clients || []).map(c => ({ id: c.airtableId, label: c.name })));
      }
    } catch (err) { console.error('Error loading clients:', err); }
  };

  // ── Format helpers ───────────────────────────────────────────────
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return parseUtcDate(dateString).toLocaleString('nl-NL', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  // ── Sprekers detecteren & hernoemen ──────────────────────────────
  // Sprekers verschijnen aan het begin van een transcript-regel, bv:
  //   "Joris: ..."           → label "Joris"
  //   "[Spreker 3]: ..."     → label "[Spreker 3]"
  //   "**Joris:** ..."       → label "Joris"
  const detectedSpeakers = useMemo(() => {
    if (!transcript) return [];
    const seen = new Set();
    const result = [];
    const lines = transcript.split('\n');
    for (const line of lines) {
      const m = line.match(/^\s*(?:\*\*)?(\[[^\]\n]{1,80}\]|[^\s:\[\*][^:\n]{0,79}?):(?:\*\*)?\s/);
      if (m) {
        const label = m[1].trim();
        if (label && !seen.has(label)) { seen.add(label); result.push(label); }
      }
    }
    return result;
  }, [transcript]);

  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Vervang één spreker overal in een tekst.
  // - "[Spreker 3]" → vervang zowel het volledige bracket-label als de inner naam ("Spreker 3").
  // - Plain naam ("Joris") → vervang met word-boundaries om geen substrings te raken.
  const applyOneRename = (text, origLabel, newName) => {
    if (!text || !origLabel || !newName) return text;
    const escOrig = escapeRegex(origLabel);
    const bracketMatch = origLabel.match(/^\[(.+)\]$/);
    if (bracketMatch) {
      let out = text.replace(new RegExp(escOrig, 'g'), newName);
      const escInner = escapeRegex(bracketMatch[1]);
      out = out.replace(new RegExp(`\\b${escInner}\\b`, 'g'), newName);
      return out;
    }
    return text.replace(new RegExp(`\\b${escOrig}\\b`, 'g'), newName);
  };

  const hasPendingRenames = useMemo(
    () => Object.entries(speakerRenames).some(([orig, name]) => name && name.trim() && name.trim() !== orig),
    [speakerRenames]
  );

  const handleApplyRenames = () => {
    const pairs = Object.entries(speakerRenames)
      .map(([orig, name]) => [orig, (name || '').trim()])
      .filter(([orig, name]) => name && name !== orig);
    if (pairs.length === 0) { toast('Geen wijzigingen', { icon: 'ℹ️' }); return; }

    let newTranscript = transcript;
    let newSummary = summary;
    let newEmailBody = emailBody;
    let newEmailSubject = emailSubject;
    let newActionItems = actionItems.slice();

    pairs.forEach(([orig, name]) => {
      newTranscript = applyOneRename(newTranscript, orig, name);
      newSummary = applyOneRename(newSummary, orig, name);
      newEmailBody = applyOneRename(newEmailBody, orig, name);
      newEmailSubject = applyOneRename(newEmailSubject, orig, name);
      newActionItems = newActionItems.map(it => applyOneRename(it, orig, name));
    });

    setTranscript(newTranscript);
    setSummary(newSummary);
    setEmailBody(newEmailBody);
    setEmailSubject(newEmailSubject);
    setActionItems(newActionItems);
    setSpeakerRenames({});
    toast.success(`${pairs.length} spreker${pairs.length === 1 ? '' : 's'} hernoemd`);
  };

  // ── Handlers: Summary & Email ────────────────────────────────────
  const handleGenerateSummary = async () => {
    if (!meeting?.transcript) { toast.error('Geen transcript beschikbaar'); return; }
    setGeneratingSummary(true);
    try {
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${meeting.id}/generate-summary`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to generate summary');
      const data = await response.json();
      setSummary(data.summary || '');
      setSummaryViewMode('preview');
      toast.success('Samenvatting gegenereerd!');
    } catch (err) { toast.error('Kon samenvatting niet genereren'); }
    finally { setGeneratingSummary(false); }
  };

  const handleGenerateEmail = async () => {
    if (!summary && !transcript) { toast.error('Geen samenvatting of transcript beschikbaar'); return; }
    setGeneratingEmail(true);
    try {
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${meeting.id}/generate-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary: summary || meeting.summary, transcript }),
      });
      if (!response.ok) throw new Error('Failed to generate email');
      const data = await response.json();
      setEmailSubject(data.subject || ''); setEmailBody(data.body || '');
      toast.success('Email concept gegenereerd!');
    } catch (err) { toast.error('Kon email niet genereren'); }
    finally { setGeneratingEmail(false); }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(`Onderwerp: ${emailSubject}\n\n${emailBody}`);
    toast.success('Email gekopieerd!');
  };

  // Samenvatting direct bewaren (los van de grote 'Meeting opslaan'-knop):
  // een handmatige correctie moet niet verloren gaan als je daarna wegklikt.
  const handleSaveSummary = async () => {
    setSummarySaving(true);
    try {
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${meeting.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setSavedSummary(summary); // dirty-check via state, niet via prop-mutatie
      setSummaryViewMode('preview');
      toast.success('Samenvatting opgeslagen');
      if (onUpdate) onUpdate();
    } catch (err) { toast.error('Kon samenvatting niet opslaan'); }
    finally { setSummarySaving(false); }
  };

  const handleCopySummary = () => {
    if (!summary) return;
    navigator.clipboard.writeText(summary);
    toast.success('Samenvatting gekopieerd!');
  };

  const handleSaveAsDraft = async () => {
    if (!emailSubject && !emailBody) { toast.error('Geen email om op te slaan'); return; }
    if (accounts.length === 0) { toast.error('Log eerst in met je Microsoft account'); return; }
    setSavingDraft(true);
    try {
      // Deelnemers (e-mailadressen uit het deelnemers-veld, exclusief jezelf) als ontvangers.
      const own = (accounts[0]?.username || '').toLowerCase();
      const recipients = (editParticipants || '')
        .split(',').map((s) => s.trim())
        .filter((s) => /\S+@\S+\.\S+/.test(s) && s.toLowerCase() !== own);
      const created = await saveEmailAsDraft(msalInstance, accounts[0], {
        subject: emailSubject || 'Follow-up meeting', body: emailBody, recipients,
      });
      if (created?.webLink) setDraftLink(created.webLink);
      toast.success(recipients.length
        ? `Concept opgeslagen in Outlook (${recipients.length} ontvanger${recipients.length === 1 ? '' : 's'})`
        : 'Email opgeslagen als concept in Outlook!');
    } catch (err) {
      toast.error(err.message?.includes('consent') ? 'Geef toestemming voor Mail.ReadWrite' : 'Kon email niet opslaan');
    } finally { setSavingDraft(false); }
  };

  const handleAdjustEmail = async () => {
    if (!emailInstruction.trim() || !emailBody) return;
    setAdjustingEmail(true);
    try {
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/adjust-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_subject: emailSubject, current_body: emailBody, instruction: emailInstruction }),
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setEmailSubject(data.subject || emailSubject); setEmailBody(data.body || emailBody);
      setEmailInstruction(''); toast.success('Email aangepast!');
    } catch (err) { toast.error('Kon email niet aanpassen'); }
    finally { setAdjustingEmail(false); }
  };

  // ── Handlers: Tasks ──────────────────────────────────────────────
  const handleExtractActions = async () => {
    if (!summary && !transcript) { toast.error('Geen transcript beschikbaar'); return; }
    setExtractingActions(true);
    try {
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${meeting.id}/extract-actions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary: summary || meeting.summary, transcript }),
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      if (data.action_items?.length > 0) {
        const { tasks: parsedTasks, suggestedAssignees: parsedAssignees } = parseActionItemsWithAssignees(data.action_items);
        setActionItems(prev => {
          const existing = new Set(prev.map(item => item.toLowerCase()));
          const newItems = parsedTasks.filter(item => !existing.has(item.toLowerCase()));
          const startIndex = prev.length;
          setTaskDestinations(prevDest => {
            const newDest = { ...prevDest };
            newItems.forEach((_, i) => { newDest[startIndex + i] = 'skip'; });
            return newDest;
          });
          setTaskAssignees(prevAssign => {
            const newAssign = { ...prevAssign };
            newItems.forEach((_, i) => {
              const originalIndex = parsedTasks.indexOf(newItems[i]);
              const suggestedName = parsedAssignees[originalIndex];
              newAssign[startIndex + i] = (suggestedName && people.length > 0) ? (findPersonByName(suggestedName, people) || '') : '';
            });
            return newAssign;
          });
          return [...prev, ...newItems];
        });
        toast.success(`${parsedTasks.length} actiepunten gevonden!`);
      } else { toast('Geen actiepunten gevonden', { icon: 'ℹ️' }); }
    } catch (err) { toast.error('Kon actiepunten niet extraheren'); }
    finally { setExtractingActions(false); }
  };

  const handleAddTask = () => {
    if (!newTask.trim()) return;
    const newIndex = actionItems.length;
    setActionItems([...actionItems, newTask.trim()]);
    setTaskDestinations(prev => ({ ...prev, [newIndex]: 'skip' }));
    setTaskAssignees(prev => ({ ...prev, [newIndex]: '' }));
    setNewTask('');
  };

  const handleRemoveTask = (index) => {
    setActionItems(actionItems.filter((_, i) => i !== index));
    const reindex = (prev) => {
      const result = {};
      Object.keys(prev).forEach(key => {
        const k = parseInt(key);
        if (k < index) result[k] = prev[k];
        else if (k > index) result[k - 1] = prev[k];
      });
      return result;
    };
    setTaskDestinations(reindex);
    setTaskAssignees(reindex);
    setTaskDetails(reindex);
  };

  const handleSetDestination = (index, destination) => {
    setTaskDestinations(prev => ({ ...prev, [index]: destination }));
    if (destination === 'airtable' && !taskDetails[index]) {
      setTaskDetails(prev => ({
        ...prev, [index]: { startDate: defaultTaskStartDate, endDate: '', estimatedHours: '', projectId: '', type: '', typeDeadline: '', prioNr: '' }
      }));
    }
  };

  const handleSetTaskDetail = (index, field, value) => {
    setTaskDetails(prev => ({
      ...prev,
      [index]: { ...(prev[index] || { startDate: '', endDate: '', estimatedHours: '', projectId: '', type: '', typeDeadline: '', prioNr: '' }), [field]: value }
    }));
  };

  const getTasksByDestination = (destination) => actionItems.filter((_, idx) => taskDestinations[idx] === destination);

  const handleSuggestAssignee = async (index) => {
    if (people.length === 0) { toast.error('Geen personen geladen'); return; }
    setSuggestingAssignee(index);
    try {
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/suggest-assignee`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_item: actionItems[index], people: people.map(p => ({ id: p.id, naam: p.naam, functie: p.functie })) }),
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      if (data.suggested_id && data.confidence !== 'none') {
        setTaskAssignees(prev => ({ ...prev, [index]: data.suggested_id }));
        toast.success(`Suggestie: ${data.suggested_naam || 'Onbekend'}`);
      } else { toast('Geen geschikte persoon gevonden', { icon: 'ℹ️' }); }
    } catch (err) { toast.error('Kon geen suggestie maken'); }
    finally { setSuggestingAssignee(null); }
  };

  const handleSaveAllTasks = async () => {
    const airtableTasks = getTasksByDestination('airtable');
    const todoTasks = getTasksByDestination('todo');
    if (airtableTasks.length === 0 && todoTasks.length === 0) { toast('Geen taken geselecteerd', { icon: 'ℹ️' }); return; }

    // Bevestigen zodra er een To Do bij een collega belandt (die krijgt een melding
    // en dat is vanuit de app niet terug te draaien).
    const mijnEmail = accounts?.[0]?.username?.toLowerCase() || '';
    const naarCollega = actionItems.some((_, idx) => {
      if (taskDestinations[idx] !== 'todo') return false;
      const assigneeId = taskAssignees[idx];
      const person = assigneeId ? people.find(p => p.id === assigneeId || p.recordId === assigneeId) : null;
      const email = (person?.email || person?.Email || '').toLowerCase();
      return Boolean(email) && email !== mijnEmail;
    });
    if (naarCollega && !(await bevestig({
      titel: 'Taken wegschrijven?',
      tekst: "Taken komen in Airtable en, voor collega's, in hun Microsoft To Do met een melding. Dat is vanuit de app niet terug te draaien.",
      knop: 'Wegschrijven',
    }))) return;

    setSavingTasks(true);
    const results = { airtable: 0, todo: 0 };
    try {
      if (airtableTasks.length > 0) {
        const airtableIndices = actionItems.map((_, idx) => idx).filter(idx => taskDestinations[idx] === 'airtable');
        const response = await authFetch(`${BACKEND_URL}/Api/tasks/create-batch`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tasks: airtableTasks.map((task, i) => {
              const taskIndex = airtableIndices[i];
              const details = taskDetails[taskIndex] || {};
              return {
                name: task, source: 'Meeting Notes', type: 'Actie',
                meeting_id: meeting.fireflies_meeting_id, meeting_date: meeting.meeting_date,
                assigneeId: taskAssignees[taskIndex] || null,
                startDate: details.startDate || null, deadline: details.endDate || null,
                estimatedHours: details.estimatedHours || null, type: details.type || null,
                typeDeadline: details.typeDeadline || null, prioNr: details.prioNr || null,
                projectId: globalTaskProject || details.projectId || selectedProject || null,
              };
            }),
          }),
        });
        if (response.ok) { const data = await response.json(); results.airtable = data.created || airtableTasks.length; }
        else { toast.error('Airtable opslaan mislukt'); }
      }

      if (todoTasks.length > 0) {
        const currentUserEmail = accounts?.[0]?.username?.toLowerCase() || '';
        const meetingBody = `Uit meeting: ${meeting.title || 'onbekend'}\nDatum: ${meeting.meeting_date ? parseUtcDate(meeting.meeting_date).toLocaleDateString('nl-NL') : 'onbekend'}`;
        const todoIndices = actionItems.map((_, idx) => idx).filter(idx => taskDestinations[idx] === 'todo');
        const ownTodoTasks = [];
        const othersTodoTasks = {};

        todoIndices.forEach((taskIndex, i) => {
          const assigneeId = taskAssignees[taskIndex];
          const person = assigneeId ? people.find(p => p.id === assigneeId || p.recordId === assigneeId) : null;
          const assigneeEmail = (person?.email || person?.Email || '').toLowerCase();
          const taskData = { title: todoTasks[i], body: meetingBody };
          if (!assigneeEmail || assigneeEmail === currentUserEmail) { ownTodoTasks.push({ name: todoTasks[i], body: meetingBody }); }
          else { if (!othersTodoTasks[assigneeEmail]) othersTodoTasks[assigneeEmail] = []; othersTodoTasks[assigneeEmail].push(taskData); }
        });

        if (ownTodoTasks.length > 0) {
          const todoResult = await createMicrosoftTodoTasks(ownTodoTasks);
          results.todo += todoResult.created.length;
        }
        for (const [email, tasks] of Object.entries(othersTodoTasks)) {
          try {
            const resp = await authFetch(`${BACKEND_URL}/Api/todo/create-for-user`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email, tasks }),
            });
            if (resp.ok) { const data = await resp.json(); results.todo += (data.created || []).length; }
            else {
              const fallbackTasks = tasks.map(t => ({ name: `[${email}] ${t.title}`, body: t.body }));
              const fallbackResult = await createMicrosoftTodoTasks(fallbackTasks);
              results.todo += fallbackResult.created.length;
              toast(`To-Do voor ${email} in je eigen lijst`, { icon: '⚠️' });
            }
          } catch (otherErr) { toast.error(`To-Do voor ${email} mislukt`); }
        }
      }

      const messages = [];
      if (results.airtable > 0) messages.push(`${results.airtable} in Airtable`);
      if (results.todo > 0) messages.push(`${results.todo} in To Do`);
      if (messages.length > 0) toast.success(`Opgeslagen: ${messages.join(', ')}`);
    } catch (err) { toast.error('Kon taken niet opslaan'); }
    finally { setSavingTasks(false); }
  };

  // ── Handlers: Project ────────────────────────────────────────────
  const handleSuggestProject = async () => {
    if (!meeting?.title && !summary) { toast.error('Geen meeting titel of samenvatting beschikbaar'); return; }
    if (projects.length === 0) { toast.error('Geen projecten geladen'); return; }
    setSuggestingProject(true);
    try {
      const projectList = projects.map(p => ({ id: p.id, name: p.fields?.Projectnr_en_naam || p.fields?.Projectnaam || 'Onbekend' }));
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${meeting.id}/suggest-project`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meeting_title: meeting?.title || '', meeting_summary: summary || meeting?.summary || '', projects: projectList }),
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      if (data.suggested_id) {
        const matchedLabel = data.suggested_name || projectList.find(p => p.id === data.suggested_id)?.name || getProjectLabel(data.suggested_id) || 'Onbekend project';
        setSuggestedProject({ id: data.suggested_id, label: matchedLabel, confidence: data.confidence, reason: data.reason });
        toast.success('Project suggestie gevonden!');
      } else { toast('Geen passend project gevonden', { icon: 'ℹ️' }); }
    } catch (err) { toast.error('Kon project suggestie niet ophalen'); }
    finally { setSuggestingProject(false); }
  };

  // ── Save & Complete ──────────────────────────────────────────────
  // Titel direct bewaren bij blur/Enter — niemand vond de grote opslaan-knop
  // hiervoor (test 20 aug), en een niet-bewaarde titel voelt als kapot.
  const saveTitleNow = async () => {
    if (!titleDirty || !meeting?.id) return;
    const titel = editTitle.trim();
    const bekend = savedTitleRef.current ?? (meeting.title || '');
    if (!titel || titel === bekend) { setTitleDirty(false); setEditTitle(bekend); return; }
    try {
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${meeting.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: titel }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      savedTitleRef.current = titel;
      setTitleDirty(false);
      toast.success('Titel opgeslagen');
      if (onUpdate) onUpdate();
    } catch { toast.error('Titel opslaan mislukt'); }
  };
  const savedTitleRef = useRef(null);

  // ── Delen met een collega ──
  const [deelOpen, setDeelOpen] = useState(false);
  const [deelKeuze, setDeelKeuze] = useState(null);
  const [deelBusy, setDeelBusy] = useState(false);
  const [deelCollegas, setDeelCollegas] = useState([]);
  useEffect(() => {
    if (!deelOpen || deelCollegas.length) return;
    authFetch(`${BACKEND_URL}/Api/overdracht/colleagues`)
      .then((r) => (r.ok ? r.json() : { colleagues: [] }))
      .then((d) => setDeelCollegas(d.colleagues || []))
      .catch(() => {});
  }, [deelOpen, deelCollegas.length]);

  // ── E-mails aan de projectnotitie koppelen (Joris 18 sep: 'ook emails toevoegen') ──
  const [mailZoekOpen, setMailZoekOpen] = useState(false);
  const [mailZoekTerm, setMailZoekTerm] = useState('');
  const [mailZoekBusy, setMailZoekBusy] = useState(false);
  const [mailHits, setMailHits] = useState(null);
  const [mailAdding, setMailAdding] = useState(null);
  const zoekMails = async () => {
    if (!mailZoekTerm.trim()) return;
    setMailZoekBusy(true);
    try { setMailHits(await searchMail(mailZoekTerm.trim(), { top: 12 })); }
    catch (e) { toast.error(`Mail zoeken mislukt: ${e.message}`); setMailHits([]); }
    finally { setMailZoekBusy(false); }
  };
  const voegMailToe = async (m) => {
    setMailAdding(m.id);
    try {
      let tekst;
      if (m.conversationId) tekst = (await getConversationText(m.conversationId, { maxChars: 8000 })).tekst;
      else { const b = await getMailBody(m.id); tekst = typeof b === 'string' ? b : (b?.text || b?.body || ''); }
      const pid = selectedProject && !String(selectedProject).startsWith('offer-') ? selectedProject : null;
      const bijlageRes = await verwerkMailBijlagen(m.id, { projectId: pid });
      const { blok: bijlageBlok, bijlagen } = bijlageRes;
      if (bijlagenMelding(bijlageRes)) toast(bijlagenMelding(bijlageRes), { icon: '⚠️', duration: 8000 });
      setNotes((prev) => `${prev ? `${prev}\n\n` : ''}— Uit e-mail: ${m.subject} (${m.from}${m.received ? `, ${String(m.received).slice(0, 10)}` : ''}) —\n${schoonMailTekst(tekst).slice(0, 6000)}${bijlageBlok ? `\n\n${bijlageBlok}` : ''}`);
      toast.success(bijlagen.length ? `E-mail + ${bijlagen.length} bijlage${bijlagen.length === 1 ? '' : 'n'} toegevoegd` : 'E-mail toegevoegd aan de notities');
    } catch (e) { toast.error(`E-mail ophalen mislukt: ${e.message}`); }
    finally { setMailAdding(null); }
  };

  // ── OneNote-notulen aan de projectnotitie koppelen ──
  const [onenoteZoekOpen, setOnenoteZoekOpen] = useState(false);
  const [onenoteZoekTerm, setOnenoteZoekTerm] = useState('');
  const [onenoteZoekBusy, setOnenoteZoekBusy] = useState(false);
  const [onenoteHits, setOnenoteHits] = useState(null);
  const [onenoteAdding, setOnenoteAdding] = useState(null);
  const zoekOnenotePaginas = async () => {
    if (!onenoteZoekTerm.trim()) return;
    setOnenoteZoekBusy(true); setOnenoteHits(null);
    try { setOnenoteHits(await searchOnenote(onenoteZoekTerm.trim())); }
    catch (e) { toast.error(`OneNote zoeken mislukt: ${e.message}`); setOnenoteHits([]); }
    finally { setOnenoteZoekBusy(false); }
  };
  const voegOnenoteToe = async (pag) => {
    setOnenoteAdding(pag.id);
    try {
      const tekst = await getOnenotePageText(pag.id);
      setNotes((prev) => `${prev ? `${prev}\n\n` : ''}— Uit OneNote: ${pag.title} —\n${String(tekst || '').slice(0, 6000)}`);
      toast.success('OneNote-notulen toegevoegd aan de notities');
    } catch (e) { toast.error(`Pagina laden mislukt: ${e.message}`); }
    finally { setOnenoteAdding(null); }
  };

  const deelMeeting = async () => {
    if (!deelKeuze?.email || !meeting?.id) return;
    setDeelBusy(true);
    try {
      const huidig = editParticipants || meeting.participants || '';
      // Exacte token-match, geen substring: ann@ zit ook in joann@ (Codex r11).
      const alIn = huidig
        .split(/[,;\s]+/)
        .some((t) => t.trim().toLowerCase() === deelKeuze.email.trim().toLowerCase());
      const nieuw = alIn ? huidig : (huidig ? `${huidig}, ${deelKeuze.email}` : deelKeuze.email);
      if (!alIn) {
        const r = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${meeting.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ participants: nieuw }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setEditParticipants(nieuw);
      }
      try { await navigator.clipboard.writeText(`${window.location.origin}/MeetingDashboard/${meeting.id}`); } catch { /* klembord optioneel */ }
      toast.success(`${deelKeuze.name || deelKeuze.email} heeft nu toegang — link gekopieerd`);
      setDeelOpen(false); setDeelKeuze(null);
      if (onUpdate) onUpdate();
    } catch (e) { toast.error(`Delen mislukt: ${e.message}`); }
    finally { setDeelBusy(false); }
  };

  const handleSave = async (newStatus) => {
    setLoading(true);
    try {
      const updatePayload = {
        summary, email_subject_draft: emailSubject, email_body_draft: emailBody,
        action_items: actionItems, status: newStatus || meeting.status,
      };
      if (titleDirty) updatePayload.title = editTitle;
      if (participantsDirty) updatePayload.participants = editParticipants;
      if (transcript !== (meeting.transcript || '')) updatePayload.transcript = transcript;

      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${meeting.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
      });
      if (!response.ok) throw new Error('Failed to save');
      setSavedSummary(summary);

      // Save tasks if any are routed
      const hasRoutedTasks = actionItems.some((_, idx) => taskDestinations[idx] !== 'skip');
      if (hasRoutedTasks) await handleSaveAllTasks();

      // Save project notes if completing and project selected.
      // Een offerte-koppeling (offer-…) heeft geen Airtable-projectrecord: de
      // notes-POST zou falen — de meeting-link hieronder werkt wél.
      if (newStatus === 'completed' && selectedProject && !String(selectedProject).startsWith('offer-')) {
        try {
          await authFetch(`${BACKEND_URL}/Api/project-notes`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              projectId: selectedProject, title: editTitle || meeting.title || 'Meeting',
              notes: notes || '', meetingDate: meeting.meeting_date,
              summary, actionItems, attendees: editParticipants || meeting.participants, source: meeting.source || 'Meeting'
            }),
          });
          toast.success('Notities opgeslagen bij project!');
        } catch (noteErr) { toast('Notities niet opgeslagen bij project', { icon: '⚠️' }); }

      }

      // Het gesprek zélf koppelen — ook aan een offerte (offer-…): het linkmodel
      // kan dat aan, alleen de notes-POST hierboven vereist een projectrecord.
      if (newStatus === 'completed' && selectedProject) {
        try {
          const linkRes = await authFetch(`${BACKEND_URL}/Api/project-flow/${selectedProject}/meetings`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ meetingId: meeting.id }),
          });
          if (!linkRes.ok) throw new Error(`HTTP ${linkRes.status}`);
        } catch (linkErr) {
          toast('Gesprek niet gekoppeld', { icon: '⚠️' });
        }
      }

      toast.success(newStatus === 'completed' ? 'Meeting afgerond!' : 'Meeting opgeslagen!');
      if (onUpdate) onUpdate();
      if (newStatus === 'completed') onClose();
    } catch (err) { toast.error('Kon meeting niet opslaan'); }
    finally { setLoading(false); }
  };

  if (!meeting) return null;

  const hasTranscript = transcript && transcript.length > 50;
  const airtableCount = getTasksByDestination('airtable').length;
  const todoCount = getTasksByDestination('todo').length;

  // Eén gedeelde JSX-boom voor beide varianten (géén inline wrapper-component —
  // dat zou bij elke render remounten en focus in invoervelden slopen).
  const frame = (
    <>
      {/* ── HEADER ────────────────────────────────────────────────── */}
      <Box sx={{ px: 3, pt: 2, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <TextField
              fullWidth variant="standard" value={editTitle}
              onChange={(e) => { setEditTitle(e.target.value); setTitleDirty(true); }}
              onBlur={saveTitleNow}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
              placeholder="Meeting titel... (Enter of wegklikken = opslaan)"
              InputProps={{ sx: { fontSize: '1.3rem', fontWeight: 600 }, disableUnderline: !titleDirty }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(meeting.meeting_date)}
                {meeting.duration && ` · ${meeting.duration} min`}
              </Typography>
              <Chip
                label={meeting.source === 'local_recording' ? 'Lokale opname' : 'Fireflies'}
                size="small" variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            </Box>
            <TextField
              fullWidth variant="standard" size="small" value={editParticipants}
              onChange={(e) => { setEditParticipants(e.target.value); setParticipantsDirty(true); }}
              placeholder="Deelnemers..."
              InputProps={{ sx: { fontSize: '0.85rem', color: 'text.secondary' }, disableUnderline: !participantsDirty, startAdornment: <Typography variant="body2" sx={{ mr: 0.5, color: 'text.disabled' }}>👥</Typography> }}
              sx={{ mt: 0.5 }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Transcript downloaden als tekst of Word (wiki-feedback Meeting notes, 9 sep). */}
            <Tooltip title="Transcript en samenvatting downloaden">
              <span>
                <Button size="small" variant="outlined" disabled={!hasTranscript} onClick={(e) => setDownloadAnker(e.currentTarget)}
                  sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  ⬇ Transcript
                </Button>
              </span>
            </Tooltip>
            <Menu anchorEl={downloadAnker} open={Boolean(downloadAnker)} onClose={() => setDownloadAnker(null)}>
              {[['docx', 'Word (.docx)'], ['txt', 'Platte tekst (.txt)']].map(([formaat, label]) => (
                <MenuItem key={formaat} onClick={async () => {
                  setDownloadAnker(null);
                  try { await downloadTranscript(meeting, { formaat, samenvatting: summary || meeting?.summary || '', transcript }); }
                  catch (err) { toast.error(`Downloaden mislukt: ${err.message}`); }
                }}>{label}</MenuItem>
              ))}
            </Menu>
            <Tooltip title="Deel deze meeting met een collega: die krijgt toegang (komt in de deelnemerslijst) en de link wordt gekopieerd.">
              <Button size="small" variant="outlined" onClick={() => setDeelOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                🔗 Delen
              </Button>
            </Tooltip>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>
        </Box>
      </Box>

      {/* Delen: toegang loopt via de deelnemerslijst (host/deelnemer/admin-ACL) */}
      <Dialog open={deelOpen} onClose={() => setDeelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Meeting delen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            De collega wordt aan de deelnemers toegevoegd en kan de meeting dan zelf openen
            (transcript, samenvatting en actiepunten). De link komt op je klembord.
          </Typography>
          <Autocomplete size="small" options={deelCollegas} autoHighlight openOnFocus
            getOptionLabel={(o) => o?.name || o?.email || ''}
            isOptionEqualToValue={(o, v) => o.email === v.email}
            value={deelKeuze} onChange={(_e, c) => setDeelKeuze(c)}
            renderInput={(params) => <TextField {...params} autoFocus label="Collega" placeholder="Kies een collega…" />} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeelOpen(false)} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" disabled={!deelKeuze || deelBusy}
            onClick={deelMeeting} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {deelBusy ? 'Delen…' : 'Toegang geven & link kopiëren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── CONTENT: Two columns ──────────────────────────────────── */}
      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden', flex: 1, minHeight: 0 }}>
        {/* LEFT: Transcript */}
        <Box sx={{ flex: '0 0 55%', px: 3, py: 2.5, overflow: 'auto', borderRight: 1, borderColor: 'divider' }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Transcript {hasTranscript && `(${(transcript.length / 1000).toFixed(1)}K tekens)`}
          </Typography>
          {hasTranscript ? (
            <Box sx={{
              fontSize: '0.88rem', lineHeight: 1.8, fontFamily: '"Segoe UI", sans-serif',
              '& p': { mb: 1.5 },
              '& strong': { color: 'primary.main' },
            }}>
              <ReactMarkdown>{transcript}</ReactMarkdown>
            </Box>
          ) : (
            <Typography color="text.secondary" fontStyle="italic" sx={{ py: 4, textAlign: 'center' }}>
              Geen transcript beschikbaar
            </Typography>
          )}
        </Box>

        {/* RIGHT: Action cards */}
        <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: 'grey.50' }}>
          {nogBezig && (
            <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ m: 2 }}>
              De transcriptie draait nog. Zodra hij klaar is verdwijnt deze regel en staat de
              echte meeting in de lijst — samenvatting en acties hebben tot die tijd geen bron.
            </Alert>
          )}
          {/* ── SAMENVATTING ─────────────────────────────────────── */}
          {!nogBezig && (
          <Accordion expanded={!!expanded.summary} onChange={() => toggleSection('summary')} disableGutters elevation={0}
            sx={{ '&:before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography fontWeight={600}>📝 Samenvatting</Typography>
                {summary && <Chip label="Beschikbaar" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mb: 1 }}>
                <Button size="small" variant="outlined" startIcon={generatingSummary ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                  onClick={handleGenerateSummary} disabled={generatingSummary || !meeting?.transcript}>
                  {generatingSummary ? 'Genereren...' : 'Genereer met AI'}
                </Button>
                <Tooltip title="Open de Offerte Generator met deze samenvatting als bron">
                  <span />
                </Tooltip>
                {summary !== savedSummary && (
                  <Button size="small" variant="contained" onClick={handleSaveSummary} disabled={summarySaving}
                    startIcon={summarySaving ? <CircularProgress size={14} color="inherit" /> : null}>
                    Samenvatting opslaan
                  </Button>
                )}
                <Tooltip title="Kopieer"><IconButton onClick={handleCopySummary} size="small" disabled={!summary}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Bekijken of zelf aanpassen (potlood)">
                <ToggleButtonGroup value={summaryViewMode} exclusive onChange={(e, val) => val && setSummaryViewMode(val)} size="small">
                  <ToggleButton value="preview"><VisibilityIcon fontSize="small" /></ToggleButton>
                  <ToggleButton value="edit"><EditIcon fontSize="small" /></ToggleButton>
                </ToggleButtonGroup>
                </Tooltip>
              </Box>
              {summaryViewMode === 'preview' ? (
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'white', maxHeight: 560, overflow: 'auto',
                  '& h1, & h2, & h3': { fontSize: '1rem', fontWeight: 600, mt: 1, mb: 0.5 }, '& p': { mb: 0.5 }, '& ul': { pl: 2 } }}>
                  {summary ? <ReactMarkdown>{summary}</ReactMarkdown> : <Typography color="text.secondary" fontStyle="italic">Geen samenvatting. Klik op "Genereer met AI" om er een te maken vanuit het transcript.</Typography>}
                </Paper>
              ) : (
                // Groeit mee met de inhoud + handmatig rekbaar (resize-hoek rechtsonder).
                <TextField fullWidth multiline minRows={12} maxRows={40} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Samenvatting..."
                  sx={{ '& textarea': { fontSize: 13, lineHeight: 1.65, resize: 'vertical' } }} />
              )}
            </AccordionDetails>
          </Accordion>
          )}

          {/* ── SPREKERS ─────────────────────────────────────────── */}
          {detectedSpeakers.length > 0 && (
            <Accordion expanded={!!expanded.speakers} onChange={() => toggleSection('speakers')} disableGutters elevation={0}
              sx={{ '&:before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography fontWeight={600}>👥 Sprekers</Typography>
                  <Chip label={detectedSpeakers.length} size="small" color="default" sx={{ height: 20, fontSize: '0.65rem' }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Geef een spreker een echte naam; de wijziging wordt overal in transcript, samenvatting, actiepunten en e-mail toegepast.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
                  {detectedSpeakers.map((label) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={label} size="small" variant="outlined" sx={{ minWidth: 110, justifyContent: 'flex-start' }} />
                      <Typography variant="body2" color="text.disabled">→</Typography>
                      <TextField size="small" fullWidth placeholder="Nieuwe naam..."
                        value={speakerRenames[label] || ''}
                        onChange={(e) => setSpeakerRenames(prev => ({ ...prev, [label]: e.target.value }))}
                        onKeyPress={(e) => { if (e.key === 'Enter' && hasPendingRenames) handleApplyRenames(); }}
                        InputProps={{ sx: { fontSize: '0.8rem' } }} />
                    </Box>
                  ))}
                </Box>
                <Button fullWidth size="small" variant="contained" startIcon={<EditIcon />}
                  onClick={handleApplyRenames} disabled={!hasPendingRenames}>
                  Toepassen
                </Button>
              </AccordionDetails>
            </Accordion>
          )}

          {/* ── PROJECT KOPPELEN ─────────────────────────────────── */}
          <Accordion expanded={!!expanded.project} onChange={() => toggleSection('project')} disableGutters elevation={0}
            sx={{ '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography fontWeight={600}>📁 Koppelen — project of offerte</Typography>
                {selectedProject && <Chip label={getProjectLabel(selectedProject)} size="small" color="success" sx={{ height: 20, fontSize: '0.65rem', maxWidth: 180 }} />}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" startIcon={suggestingProject ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                  onClick={handleSuggestProject} disabled={suggestingProject || projects.length === 0}>
                  AI Suggestie
                </Button>
                <Button size="small" variant="outlined"
                  onClick={() => { if (leadClients.length === 0) loadLeadClients(); setStartProjectOpen(true); }}
                  startIcon={<AutoAwesomeIcon />}
                  sx={{ borderColor: '#7c3aed', color: '#7c3aed', '&:hover': { borderColor: '#6d28d9', backgroundColor: '#f3e8ff' } }}>
                  Project starten
                </Button>
                <Button size="small" variant="outlined"
                  onClick={() => { if (leadClients.length === 0) loadLeadClients(); setStartProjectOpen(true); }}
                  sx={{ borderColor: '#0891b2', color: '#0e7490', '&:hover': { borderColor: '#0e7490', backgroundColor: '#ecfeff' } }}>
                  💡 Kans/lead aanmaken
                </Button>
                {/* Verplaatst uit de samenvatting-balk: koppelen en offerte-maken
                    horen bij elkaar (feedback: 'verwarrend dat het bij beide staat'). */}
                <Button size="small" variant="outlined" disabled={!summary}
                  onClick={() => navigate('/offertes', {
                    state: {
                      sourceText: `Meeting "${meeting?.title || ''}"${meeting?.meeting_date ? ` (${String(meeting.meeting_date).slice(0, 10)})` : ''}\n\n${summary || ''}`,
                      sourceLabel: meeting?.title || 'Meeting',
                    },
                  })}
                  sx={{ textTransform: 'none' }}>
                  🧾 Maak offerte hiervan
                </Button>
              </Box>

              {suggestedProject && !selectedProject && (
                <Alert severity="success" sx={{ mb: 1.5 }}
                  action={<Button color="inherit" size="small" onClick={() => { setSelectedProject(suggestedProject.id); setSelectedProjectInput(suggestedProject.label || ''); toast.success('Project gekoppeld'); }}>Accepteren</Button>}>
                  <Typography variant="body2" fontWeight={500}>💡 {suggestedProject.label}</Typography>
                  {suggestedProject.reason && <Typography variant="caption">{suggestedProject.reason}</Typography>}
                </Alert>
              )}

              {/* Server-side zoeken: projecten (óók op draaidag-werknaam, zoals
                  "kadijk" → 9677) én open Gripp-offertes/leads in één lijst. */}
              <Autocomplete
                options={koppelOpties} loading={koppelZoekt} size="small"
                filterOptions={(x) => x}
                groupBy={(o) => (o.type === 'offerte' ? 'Offertes & kansen' : o.type === 'lead' ? 'Leads & offerte-projecten' : 'Projecten')}
                getOptionLabel={(o) => o.label || ''}
                value={selectedProject ? { id: selectedProject, label: selectedProjectInput || getProjectLabel(selectedProject), type: 'project' } : null}
                inputValue={selectedProjectInput}
                onInputChange={(_, value, reason) => { setSelectedProjectInput(value); if (reason === 'input') zoekKoppelOpties(value); }}
                onChange={(_, option) => { setSelectedProject(option?.id || ''); setSelectedProjectInput(option?.label || ''); setSuggestedProject(null); }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography variant="body2">{o.label}</Typography>
                      {o.sub && <Typography variant="caption" color="text.secondary">{o.sub}</Typography>}
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Project of offerte" placeholder="Zoek op naam, nummer, klant of werknaam…" />}
                sx={{ mb: 1.5 }}
              />
              {selectedProject && !String(selectedProject).startsWith('offer-') && (
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Button size="small" variant="contained" disableElevation
                    onClick={() => navigate(`/projects/${selectedProject}`)}
                    sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#4f46e5', '&:hover': { bgcolor: '#4338ca' } }}>
                    📂 Project openen
                  </Button>
                  <Button size="small" variant="outlined"
                    onClick={() => navigate(`/projects/${selectedProject}?paneel=correspondentie`)}
                    sx={{ textTransform: 'none' }}>
                    ✉️ Correspondentie koppelen
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Verder opstarten in de projectflow: mails, offerte, planning.
                  </Typography>
                </Stack>
              )}
              {String(selectedProject).startsWith('offer-') && (
                <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
                  Gekoppeld aan een offerte: de notities blijven bij het gesprek zelf
                  (offertes hebben geen projectpagina). Wordt de offerte later een
                  project, koppel het gesprek dan opnieuw aan dat project.
                </Alert>
              )}

              <TextField fullWidth multiline rows={3} label="Notities voor project" size="small"
                value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Relevante notities..." />

              {/* E-mails erbij: zoek in je eigen mailbox, het gesprek gaat opgeschoond mee in de notitie */}
              <Button size="small" variant="text" onClick={() => setMailZoekOpen((v) => !v)}
                sx={{ textTransform: 'none', fontWeight: 700, mt: 0.5, mr: 1 }}>
                ✉️ {mailZoekOpen ? 'Verberg mail-zoeker' : 'E-mail toevoegen aan de notities'}
              </Button>
              {mailZoekOpen && (
                <Box sx={{ mt: 0.5, p: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <Stack direction="row" spacing={1}>
                    <TextField size="small" fullWidth placeholder="Zoek in je mailbox (klantnaam, onderwerp, projectnummer)…"
                      value={mailZoekTerm} onChange={(e) => setMailZoekTerm(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); zoekMails(); } }} />
                    <Button size="small" variant="outlined" onClick={zoekMails} disabled={mailZoekBusy || !mailZoekTerm.trim()}
                      sx={{ textTransform: 'none', fontWeight: 700 }}>
                      {mailZoekBusy ? <CircularProgress size={15} /> : 'Zoek'}
                    </Button>
                  </Stack>
                  {mailHits !== null && (
                    <Stack spacing={0.5} sx={{ mt: 1, maxHeight: 220, overflowY: 'auto' }}>
                      {mailHits.length === 0 && <Typography variant="caption" color="text.secondary">Niets gevonden.</Typography>}
                      {mailHits.map((m) => (
                        <Stack key={m.id} direction="row" spacing={1} alignItems="center"
                          sx={{ bgcolor: '#fff', border: '1px solid #eee', borderRadius: 1, px: 1, py: 0.5 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" noWrap>{m.subject}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{m.from} · {m.received ? String(m.received).slice(0, 10) : ''} — {m.preview}</Typography>
                          </Box>
                          <Button size="small" disabled={mailAdding === m.id} onClick={() => voegMailToe(m)}
                            sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0, flexShrink: 0 }}>
                            {mailAdding === m.id ? <CircularProgress size={13} /> : '+ toevoegen'}
                          </Button>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}

              {/* OneNote-notulen erbij: de paginatekst gaat mee in de projectnotitie */}
              <Button size="small" variant="text" onClick={() => setOnenoteZoekOpen((v) => !v)}
                sx={{ textTransform: 'none', fontWeight: 700, mt: 0.5 }}>
                📓 {onenoteZoekOpen ? 'Verberg OneNote-zoeker' : 'OneNote-notulen toevoegen aan de notities'}
              </Button>
              {onenoteZoekOpen && (
                <Box sx={{ mt: 0.5, p: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <Stack direction="row" spacing={1}>
                    <TextField size="small" fullWidth placeholder="Zoek in OneNote (klantnaam of onderwerp)…"
                      value={onenoteZoekTerm} onChange={(e) => setOnenoteZoekTerm(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); zoekOnenotePaginas(); } }} />
                    <Button size="small" variant="outlined" onClick={zoekOnenotePaginas} disabled={onenoteZoekBusy || !onenoteZoekTerm.trim()}
                      sx={{ textTransform: 'none', fontWeight: 700 }}>
                      {onenoteZoekBusy ? <CircularProgress size={15} /> : 'Zoek'}
                    </Button>
                  </Stack>
                  {onenoteHits !== null && (
                    <Stack spacing={0.5} sx={{ mt: 1, maxHeight: 200, overflowY: 'auto' }}>
                      {onenoteHits.length === 0 && <Typography variant="caption" color="text.secondary">Niets gevonden.</Typography>}
                      {onenoteHits.map((pag) => (
                        <Stack key={pag.id} direction="row" spacing={1} alignItems="center"
                          sx={{ bgcolor: '#fff', border: '1px solid #eee', borderRadius: 1, px: 1, py: 0.5 }}>
                          <Typography variant="body2" noWrap sx={{ flex: 1 }}>{pag.title}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{pag.modified ? String(pag.modified).slice(0, 10) : ''}</Typography>
                          <Button size="small" disabled={onenoteAdding === pag.id} onClick={() => voegOnenoteToe(pag)}
                            sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>
                            {onenoteAdding === pag.id ? <CircularProgress size={13} /> : '+ toevoegen'}
                          </Button>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          <StartProjectDialog
            open={startProjectOpen}
            onClose={() => setStartProjectOpen(false)}
            meeting={meeting}
            clients={leadClients}
            onCreated={(project) => {
              if (!project) return;
              setProjects((prev) => [project, ...prev]);
              setSelectedProject(project.id);
              setSelectedProjectInput(project.fields?.Projectnr_en_naam || project.fields?.Projectnaam || '');
            }}
          />

          {/* ── ACTIEPUNTEN ──────────────────────────────────────── */}
          <Accordion expanded={!!expanded.tasks} onChange={() => toggleSection('tasks')} disableGutters elevation={0}
            sx={{ '&:before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography fontWeight={600}>✅ Actiepunten</Typography>
                {actionItems.length > 0 && <Chip label={actionItems.length} size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <Button size="small" variant="outlined" startIcon={extractingActions ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                  onClick={handleExtractActions} disabled={extractingActions}>
                  Extraheer met AI
                </Button>
                {actionItems.length > 0 && (
                  <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
                    onClick={async () => { if (await bevestig({ titel: 'Alle taken wissen?', tekst: 'De geëxtraheerde actiepunten verdwijnen uit deze lijst.', knop: 'Wissen', gevaarlijk: true })) { setActionItems([]); setTaskDestinations({}); setTaskAssignees({}); setTaskDetails({}); } }}>
                    Wissen
                  </Button>
                )}
              </Box>

              {/* Add task */}
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
                <TextField fullWidth size="small" value={newTask} onChange={(e) => setNewTask(e.target.value)}
                  placeholder="Nieuwe taak..." onKeyPress={(e) => e.key === 'Enter' && handleAddTask()} />
                <IconButton onClick={handleAddTask} color="primary" size="small"><AddIcon /></IconButton>
              </Box>

              {/* Global project for Airtable tasks */}
              {airtableCount > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, backgroundColor: 'white' }}>
                  <Typography variant="caption" fontWeight={500}>📁 Project voor Airtable taken:</Typography>
                  <Autocomplete size="small" options={projectOptionsWithNone} loading={loadingProjects}
                    value={getProjectOption(globalTaskProject)} inputValue={globalTaskProjectInput}
                    onInputChange={(_, value) => setGlobalTaskProjectInput(value)}
                    onChange={(_, option) => { setGlobalTaskProject(option?.id || ''); setGlobalTaskProjectInput(option?.label || ''); }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} placeholder="Zoek project..." />}
                    sx={{ mt: 0.5 }}
                  />
                </Paper>
              )}

              {/* Taak-kaarten: taaktekst op volle breedte, daaronder Wie + duidelijke
                  bestemmings-keuze (i.p.v. drie naamloze AT/TD/— radio-kolommen). */}
              {actionItems.length > 0 && (
                <Stack spacing={1}>
                  {actionItems.map((task, index) => {
                    const dest = taskDestinations[index] || null;
                    return (
                      <Paper key={index} variant="outlined" sx={{ p: 1.5, backgroundColor: 'white',
                        borderLeft: '3px solid', borderLeftColor: dest === 'airtable' ? '#526075' : dest === 'todo' ? '#15803d' : '#e5e7eb' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <TextField fullWidth multiline size="small" value={task} variant="standard" placeholder="Taakomschrijving…"
                            onChange={(e) => setActionItems(prev => prev.map((t, i) => i === index ? e.target.value : t))}
                            InputProps={{ disableUnderline: true, sx: { fontSize: '0.85rem', fontWeight: 500 } }} />
                          <IconButton size="small" onClick={() => handleRemoveTask(index)} sx={{ p: 0.3, flexShrink: 0, opacity: 0.5, '&:hover': { color: 'error.main', opacity: 1 } }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          <Autocomplete size="small" options={people} getOptionLabel={(p) => p.naam || ''} value={people.find(p => p.id === taskAssignees[index]) || null}
                            onChange={(_, person) => setTaskAssignees(prev => ({ ...prev, [index]: person?.id || '' }))}
                            renderInput={(params) => <TextField {...params} placeholder="Wie?" InputProps={{ ...params.InputProps, sx: { ...params.InputProps?.sx, fontSize: '0.8rem' } }} />}
                            sx={{ minWidth: 160, flex: 1, maxWidth: 240 }}
                          />
                          <Tooltip title="AI suggestie">
                            <IconButton size="small" onClick={() => handleSuggestAssignee(index)} disabled={suggestingAssignee === index} sx={{ p: 0.3 }}>
                              {suggestingAssignee === index ? <CircularProgress size={12} /> : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                            </IconButton>
                          </Tooltip>
                          <ToggleButtonGroup size="small" exclusive value={dest} onChange={(_e, v) => v && handleSetDestination(index, v)}
                            sx={{ ml: 'auto', '& .MuiToggleButton-root': { py: 0.3, px: 1.1, fontSize: 11, textTransform: 'none', fontWeight: 700 } }}>
                            <ToggleButton value="airtable">📋 Airtable-taak</ToggleButton>
                            <ToggleButton value="todo">✅ To-do</ToggleButton>
                            <ToggleButton value="skip">Overslaan</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                        {dest === 'airtable' && (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25, p: 1, borderRadius: 1, backgroundColor: 'primary.50' }}>
                            <TextField size="small" label="Start" type="date" value={taskDetails[index]?.startDate || ''} onChange={(e) => handleSetTaskDetail(index, 'startDate', e.target.value)}
                              InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
                            <TextField size="small" label="Deadline" type="date" value={taskDetails[index]?.endDate || ''} onChange={(e) => handleSetTaskDetail(index, 'endDate', e.target.value)}
                              InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
                            <TextField size="small" label="Uren" type="number" value={taskDetails[index]?.estimatedHours || ''} onChange={(e) => handleSetTaskDetail(index, 'estimatedHours', e.target.value)}
                              sx={{ width: 70 }} />
                            <Autocomplete size="small" options={TASK_TYPE_OPTIONS} value={taskDetails[index]?.type || null}
                              onChange={(_, val) => handleSetTaskDetail(index, 'type', val || '')}
                              renderInput={(params) => <TextField {...params} label="Type" />} sx={{ width: 130 }} />
                            <Autocomplete size="small" options={TASK_PRIO_OPTIONS} value={taskDetails[index]?.prioNr || null}
                              onChange={(_, val) => handleSetTaskDetail(index, 'prioNr', val || '')}
                              renderInput={(params) => <TextField {...params} label="Prio" />} sx={{ width: 130 }} />
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              )}

              {/* Save tasks button */}
              {(airtableCount > 0 || todoCount > 0) && (
                <Button fullWidth variant="contained" size="small" sx={{ mt: 1.5 }}
                  startIcon={savingTasks ? <CircularProgress size={14} /> : <SaveIcon />}
                  onClick={handleSaveAllTasks} disabled={savingTasks}>
                  Taken opslaan ({airtableCount > 0 ? `${airtableCount} AT` : ''}{airtableCount > 0 && todoCount > 0 ? ' + ' : ''}{todoCount > 0 ? `${todoCount} TD` : ''})
                </Button>
              )}
            </AccordionDetails>
          </Accordion>

          {/* ── FOLLOW-UP EMAIL ──────────────────────────────────── */}
          <Accordion expanded={!!expanded.email} onChange={() => toggleSection('email')} disableGutters elevation={0}
            sx={{ '&:before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography fontWeight={600}>✉️ Follow-up Email</Typography>
                {emailSubject && <Chip label="Concept" size="small" color="info" sx={{ height: 20, fontSize: '0.65rem' }} />}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
                <Button size="small" variant="outlined" startIcon={generatingEmail ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                  onClick={handleGenerateEmail} disabled={generatingEmail}>
                  Genereer met AI
                </Button>
                <Tooltip title="Opslaan als concept in Outlook (deelnemers worden als ontvangers ingevuld)">
                  <IconButton onClick={handleSaveAsDraft} size="small" disabled={savingDraft || (!emailSubject && !emailBody)}>
                    {savingDraft ? <CircularProgress size={16} /> : <DraftsIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Kopieer email">
                  <IconButton onClick={handleCopyEmail} size="small" disabled={!emailSubject && !emailBody}><ContentCopyIcon fontSize="small" /></IconButton>
                </Tooltip>
                {draftLink && (
                  <Button size="small" variant="outlined" color="success" href={draftLink} target="_blank" rel="noopener"
                    sx={{ textTransform: 'none', fontWeight: 700, ml: 'auto' }}>
                    Open concept in Outlook ↗
                  </Button>
                )}
              </Box>
              <TextField fullWidth label="Onderwerp" size="small" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} sx={{ mb: 1 }} />
              <TextField fullWidth multiline rows={4} label="Email inhoud" size="small" value={emailBody} onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Beste [naam],&#10;&#10;Bedankt voor de meeting..." sx={{ mb: 1.5 }} />
              <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'white' }}>
                <Typography variant="caption" color="text.secondary">Pas de email aan:</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  <TextField fullWidth size="small" value={emailInstruction} onChange={(e) => setEmailInstruction(e.target.value)}
                    placeholder="bijv. 'Maak korter' of 'Meer formeel'..." onKeyPress={(e) => e.key === 'Enter' && handleAdjustEmail()} />
                  <Button size="small" variant="outlined" startIcon={adjustingEmail ? <CircularProgress size={14} /> : <SendIcon />}
                    onClick={handleAdjustEmail} disabled={adjustingEmail || !emailInstruction.trim()}>
                    Pas aan
                  </Button>
                </Box>
              </Paper>
            </AccordionDetails>
          </Accordion>

        </Box>
      </DialogContent>

      {/* ── BOTTOM BAR ────────────────────────────────────────────── */}
      <Box sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" color="inherit" size="small" startIcon={<ArchiveIcon />}
            onClick={() => { onClose(); }}>
            Sluiten
          </Button>
          {onDelete && (
            <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />}
              onClick={async () => { if (await bevestig({ titel: 'Meeting verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true })) onDelete(meeting.id); }}>
              Verwijderen
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip label={meeting.status === 'new' ? 'Nieuw' : meeting.status === 'review_needed' ? 'Review' : meeting.status === 'completed' ? 'Afgerond' : meeting.status}
            color={meeting.status === 'completed' ? 'success' : meeting.status === 'review_needed' ? 'secondary' : 'info'} size="small" />
          <Button variant="outlined" onClick={() => handleSave('review_needed')} disabled={loading}
            startIcon={loading ? <CircularProgress size={14} /> : <SaveIcon />} size="small">
            Opslaan
          </Button>
          <Button variant="contained" color="success" onClick={() => handleSave('completed')} disabled={loading}
            startIcon={loading ? <CircularProgress size={14} /> : <CheckCircleIcon />} size="small">
            Afronden
          </Button>
        </Box>
      </Box>
    </>
  );

  if (variant === 'page') {
    // Lucht rondom + kaart-gevoel — de werk-pagina plakt niet tegen de randen.
    return (
      <Box sx={{ height: 'calc(100vh - 44px)', p: { xs: 0, md: 2.5 }, bgcolor: '#f6f7f8', minWidth: 0, flex: 1 }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper',
          borderRadius: { xs: 0, md: 2 }, border: { md: '1px solid #e5e7eb' }, overflow: 'hidden',
          boxShadow: { md: '0 10px 30px rgba(45,51,53,0.05)' } }}>
          {frame}
        </Box>
      </Box>
    );
  }
  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth
      PaperProps={{ sx: { maxWidth: 1200, minHeight: '85vh', maxHeight: '92vh' } }}
    >
      {frame}
    </Dialog>
  );
}

export default MeetingDetail;
